import { useCallback, useEffect, useState } from "react";
import {
  api,
  ApiError,
  type AuthUser,
  type Profile,
  type SessionResponse,
  type SignupPending,
  type SignupVerified,
} from "@/lib/api";
import { announceWelcomeBonus } from "@/lib/welcomeBonus";
import { currentLanguage } from "@/i18n";

export type { Profile };

/**
 * Session state backed by the httpOnly cookie the API sets. There is no token
 * in JavaScript to read or refresh — the browser attaches the cookie, and
 * GET /auth/me restores the session on load.
 */
export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);

  const applySession = useCallback((session: SessionResponse) => {
    setUser(session.user);
    setProfile(session.profile);
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      applySession(await api.get<SessionResponse>("/auth/me"));
    } catch (err) {
      // A 401 just means the cookie expired; anything else is worth surfacing.
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
        setProfile(null);
      } else {
        console.error("Failed to refresh profile:", err);
      }
    }
  }, [applySession]);

  useEffect(() => {
    let mounted = true;

    api
      .get<SessionResponse>("/auth/me")
      .then((session) => {
        if (!mounted) return;
        applySession(session);
      })
      .catch((err) => {
        if (!mounted) return;
        console.error("Auth init failed:", err);
        setUser(null);
        setProfile(null);
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
        setReady(true);
      });

    return () => {
      mounted = false;
    };
  }, [applySession]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      applySession(await api.post<SessionResponse>("/auth/login", { email, password }));
    },
    [applySession]
  );

  /** Emails a verification code; the account exists only after verifySignup. */
  const signUp = useCallback(
    (email: string, password: string, fullName?: string) =>
      // Only called once the form's 18+ and Terms box is ticked.
      api.post<SignupPending>("/auth/signup", {
        email,
        password,
        fullName,
        acceptTerms: true,
        // Recorded with the acceptance: which language the Terms were read in.
        language: currentLanguage(),
      }),
    []
  );

  const resendSignupCode = useCallback(
    (email: string) => api.post<SignupPending>("/auth/signup/resend", { email }),
    []
  );

  const verifySignup = useCallback(
    async (email: string, code: string) => {
      const verified = await api.post<SignupVerified>("/auth/signup/verify", { email, code });
      applySession(verified);
      // Points from a purchase made before signing up (a payment through one
      // of our partner shops, or a signed-out checkout) are shown once.
      if (verified.bonus && verified.bonus.points > 0) announceWelcomeBonus(verified.bonus);
    },
    [applySession]
  );

  const requestPasswordReset = useCallback(
    (email: string) =>
      api.post<{ ok: true; expiresInMinutes: number }>("/auth/forgot-password", { email }),
    []
  );

  const resetPassword = useCallback(
    async (token: string, password: string) => {
      applySession(await api.post<SessionResponse>("/auth/reset-password", { token, password }));
    },
    [applySession]
  );

  const signOut = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(null);
      setProfile(null);
    }
  }, []);

  /** Apply a balance the server already returned, without another round-trip. */
  const setCredits = useCallback((credits: number) => {
    setProfile((prev) => (prev ? { ...prev, credits } : prev));
  }, []);

  return {
    user,
    profile,
    loading,
    ready,
    signIn,
    signUp,
    resendSignupCode,
    verifySignup,
    requestPasswordReset,
    resetPassword,
    signOut,
    refreshProfile,
    setCredits,
    setProfile,
  };
}
